import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import MiniMap from '../../components/MiniMap';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Grid, Card, Button, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Badge, CircularProgress, Divider, Alert, Radio, Drawer,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon            from '@mui/icons-material/Add';
import RemoveIcon         from '@mui/icons-material/Remove';
import ShoppingCartIcon   from '@mui/icons-material/ShoppingCart';
import AccessTimeIcon     from '@mui/icons-material/AccessTime';
import LocalShippingIcon  from '@mui/icons-material/LocalShipping';
import StorefrontIcon     from '@mui/icons-material/Storefront';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import LocationOnIcon     from '@mui/icons-material/LocationOn';
import StarIcon           from '@mui/icons-material/Star';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import LockIcon           from '@mui/icons-material/Lock';
import CloseIcon          from '@mui/icons-material/Close';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import LocalPizzaIcon     from '@mui/icons-material/LocalPizza';
import LunchDiningIcon    from '@mui/icons-material/LunchDining';
import SetMealIcon        from '@mui/icons-material/SetMeal';
import CakeIcon           from '@mui/icons-material/Cake';
import LocalBarIcon       from '@mui/icons-material/LocalBar';
import SpaIcon            from '@mui/icons-material/Spa';
import DinnerDiningIcon   from '@mui/icons-material/DinnerDining';
import KebabDiningIcon    from '@mui/icons-material/KebabDining';
import BakeryDiningIcon   from '@mui/icons-material/BakeryDining';
import RamenDiningIcon    from '@mui/icons-material/RamenDining';
import RestaurantIcon     from '@mui/icons-material/Restaurant';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CelebrationIcon    from '@mui/icons-material/Celebration';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import restaurantService from '../../services/restaurant.service';
import userService       from '../../services/user.service';
import { computeIsOpen, todayHoursLabel, slotsForDay, deliveryWindows, mayotteDate } from '../../utils/date';
import { useAuth }       from '../../context/AuthContext';

// ── Stripe init (une seule fois hors du composant) ───────────────────────────
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ── Palette Usafiri ───────────────────────────────────────────────────────────
const C = {
  purple:  '#552583',
  purpleL: '#7a3aad',
  purpleD: '#33154f',
  gold:    '#FDB927',
  bg:      '#f0eee9',
  card:    '#ffffff',
  text:    '#1a1a1a',
  soft:    '#4a4a4a',
  muted:   '#9a9a9a',
  green:   '#22c55e',
  red:     '#ef4444',
  orange:  '#f59e0b',
};

const getCategoryIcon = (category, color = '#552583', size = 20) => {
  const lower = (category || '').toLowerCase();
  const sx = { fontSize: size, color };
  if (lower.includes('pizza'))    return <LocalPizzaIcon sx={sx} />;
  if (lower.includes('burger'))   return <LunchDiningIcon sx={sx} />;
  if (lower.includes('sushi') || lower.includes('japon')) return <SetMealIcon sx={sx} />;
  if (lower.includes('dessert') || lower.includes('sucr')) return <CakeIcon sx={sx} />;
  if (lower.includes('boisson') || lower.includes('drink')) return <LocalBarIcon sx={sx} />;
  if (lower.includes('salade'))   return <SpaIcon sx={sx} />;
  if (lower.includes('pâte') || lower.includes('pasta') || lower.includes('italien')) return <DinnerDiningIcon sx={sx} />;
  if (lower.includes('poulet') || lower.includes('chicken') || lower.includes('viande') || lower.includes('grill')) return <KebabDiningIcon sx={sx} />;
  if (lower.includes('poisson') || lower.includes('fish')) return <SetMealIcon sx={sx} />;
  if (lower.includes('petit') || lower.includes('breakfast')) return <BakeryDiningIcon sx={sx} />;
  if (lower.includes('entrée') || lower.includes('apéritif')) return <BakeryDiningIcon sx={sx} />;
  if (lower.includes('soupe')) return <RamenDiningIcon sx={sx} />;
  if (lower.includes('taco') || lower.includes('mexic')) return <LunchDiningIcon sx={sx} />;
  if (lower.includes('indien') || lower.includes('curry')) return <RamenDiningIcon sx={sx} />;
  if (lower.includes('chinois') || lower.includes('wok')) return <RamenDiningIcon sx={sx} />;
  return <RestaurantIcon sx={sx} />;
};

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Formulaire de paiement Stripe (doit être dans <Elements>) ────────────────
const StripePaymentForm = ({ onPaid, onError, total, cartTotal, deliveryFee, serviceFee, distanceKm, orderType, paymentDone }) => {
  const stripe   = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setPaying(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
      if (error) {
        onError(error.message || 'Paiement refusé. Vérifiez vos informations.');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onPaid(paymentIntent.id);
      } else {
        onError('Statut du paiement inconnu. Contactez le support.');
      }
    } catch (e) {
      onError('Erreur technique lors du paiement.');
    }
    setPaying(false);
  };

  const subtotal = cartTotal;

  return (
    <Box>
      {/* Récap montant détaillé */}
      <Box sx={{ mb: 3, p: 2, borderRadius: '14px', bgcolor: `${C.purple}06`, border: `1px solid ${C.purple}15` }}>
        <Box display="flex" justifyContent="space-between" mb={0.8}>
          <Typography variant="body2" sx={{ color: C.muted }}>Sous-total articles</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: C.text }}>{fmt(subtotal)} €</Typography>
        </Box>
        {orderType === 'delivery' && (
          <Box display="flex" justifyContent="space-between" mb={0.8}>
            <Box>
              <Typography variant="body2" sx={{ color: C.muted }}>
                Livraison{distanceKm ? ` (${distanceKm} km)` : ''}
              </Typography>
              {distanceKm && (
                <Typography variant="caption" sx={{ color: C.muted, fontSize: '0.65rem' }}>
                  Tarif Usafiri · 2,50 € / 5 km
                </Typography>
              )}
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: C.orange }}>{fmt(deliveryFee)} €</Typography>
          </Box>
        )}
        {serviceFee > 0 && (
          <Box display="flex" justifyContent="space-between" mb={0.8}>
            <Typography variant="body2" sx={{ color: C.muted }}>Frais de service</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: C.soft }}>{fmt(serviceFee)} €</Typography>
          </Box>
        )}
        <Divider sx={{ borderColor: `rgba(85,37,131,0.1)`, mb: 1 }} />
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body1" fontWeight={900} sx={{ color: C.text }}>Total à payer</Typography>
          <Typography variant="h6" fontWeight={900} sx={{ color: C.purple }}>{fmt(total)} €</Typography>
        </Box>
      </Box>

      {/* Stripe PaymentElement */}
      <Box sx={{
        p: 2, borderRadius: '16px', border: `1px solid rgba(85,37,131,0.15)`, bgcolor: 'white',
        mb: 2.5,
        '& .p-Input': { borderRadius: '10px' },
      }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <LockIcon sx={{ fontSize: 16, color: C.muted }} />
          <Typography variant="caption" sx={{ color: C.muted, fontWeight: 600 }}>Paiement sécurisé par Stripe</Typography>
        </Box>
        <PaymentElement options={{ layout: 'tabs' }} />
      </Box>

      <Button fullWidth variant="contained" size="large"
        onClick={handlePay}
        disabled={!stripe || paying || paymentDone}
        sx={{
          py: 1.8, borderRadius: '14px', textTransform: 'none', fontWeight: 900, fontSize: '1rem',
          background: `linear-gradient(135deg, ${C.purple}, ${C.gold})`,
          boxShadow: `0 6px 20px ${C.purple}40`,
          '&:hover': { boxShadow: `0 8px 28px ${C.purple}55` },
          '&:disabled': { background: 'rgba(0,0,0,0.12)', boxShadow: 'none' },
        }}>
        {paying ? <CircularProgress size={22} sx={{ color: 'white' }} /> : <><LockIcon sx={{ fontSize: 16, mr: 0.5 }} /> Payer {fmt(total)} EUR</>}
      </Button>

      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: C.muted, mt: 1.5 }}>
        Vos données bancaires sont chiffrées et sécurisées
      </Typography>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modale de personnalisation d'un plat avec options (style Uber Eats)
const CustomizeDialog = ({ open, item, accentColor, onClose, onAdd }) => {
  const [picks, setPicks] = useState({}); // { [group_id]: [choice_id, ...] }
  const [quantity, setQuantity] = useState(1);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && item) {
      // Pre-selection : pour un groupe single avec min>=1, prendre le premier choix
      const init = {};
      (item.option_groups || []).forEach(g => {
        if (g.selection_type === 'single' && g.min_select >= 1 && g.choices.length > 0) {
          init[g.id] = [g.choices[0].id];
        } else {
          init[g.id] = [];
        }
      });
      setPicks(init);
      setQuantity(1);
      setErr('');
    }
  }, [open, item]);

  if (!item) return null;

  const toggleChoice = (group, choiceId) => {
    setErr('');
    setPicks(prev => {
      const current = prev[group.id] || [];
      if (group.selection_type === 'single') {
        return { ...prev, [group.id]: [choiceId] };
      }
      const already = current.includes(choiceId);
      if (already) return { ...prev, [group.id]: current.filter(id => id !== choiceId) };
      // Pour multiple : respecter max_select
      if (group.max_select > 0 && current.length >= group.max_select) return prev;
      return { ...prev, [group.id]: [...current, choiceId] };
    });
  };

  // Calcul live du prix
  let delta = 0;
  const snapshot = [];
  const payload = [];
  (item.option_groups || []).forEach(g => {
    const chosenIds = picks[g.id] || [];
    if (chosenIds.length === 0) return;
    const chosenChoices = g.choices.filter(c => chosenIds.includes(c.id));
    const groupSnap = { group_name: g.name, choices: [] };
    chosenChoices.forEach(c => {
      const d = parseFloat(c.price_delta) || 0;
      delta += d;
      groupSnap.choices.push({ name: c.name, price_delta: d });
    });
    snapshot.push(groupSnap);
    payload.push({ group_id: g.id, choice_ids: chosenIds });
  });
  const basePrice = parseFloat(item.price) || 0;
  const unitPrice = basePrice + delta;
  const linePrice = unitPrice * quantity;

  const validateAndAdd = () => {
    for (const g of item.option_groups || []) {
      const n = (picks[g.id] || []).length;
      if (n < (g.min_select || 0)) { setErr(`Sélectionnez au moins ${g.min_select} option(s) dans "${g.name}"`); return; }
    }
    for (let i = 0; i < quantity; i++) onAdd(item, { delta, snapshot, payload });
    onClose();
  };

  const fmtPrice = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
      <DialogTitle sx={{ pt: 2.5, px: 2.5, pb: 1.5 }}>
        <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', letterSpacing: '-0.01em' }}>
          {item.name}
        </Typography>
        {item.description && (
          <Typography sx={{ fontSize: '0.78rem', color: '#666', mt: 0.3 }}>
            {item.description}
          </Typography>
        )}
        <Typography sx={{ fontSize: '0.86rem', color: accentColor, fontWeight: 800, mt: 0.5 }}>
          À partir de {fmtPrice(basePrice)} €
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, py: '8px !important', maxHeight: '55vh', overflowY: 'auto' }}>
        {(item.option_groups || []).map((g) => {
          const required = g.min_select >= 1;
          return (
            <Box key={g.id} sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.88rem' }}>{g.name}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: required ? '#b91c1c' : '#666', fontWeight: 600 }}>
                  {required ? 'Requis' : g.selection_type === 'multiple' ? `${g.min_select}-${g.max_select}` : 'Optionnel'}
                </Typography>
              </Box>
              {g.choices.map(c => {
                const isSelected = (picks[g.id] || []).includes(c.id);
                return (
                  <Box key={c.id} onClick={() => toggleChoice(g, c.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      py: 1.2, px: 1.5, mb: 0.6, borderRadius: '10px', cursor: 'pointer',
                      border: `1.5px solid ${isSelected ? accentColor : 'rgba(0,0,0,0.06)'}`,
                      background: isSelected ? `${accentColor}08` : 'transparent',
                      transition: 'all 0.15s',
                    }}>
                    <Box display="flex" alignItems="center" gap={1.2} flex={1}>
                      <Box sx={{
                        width: 18, height: 18, borderRadius: g.selection_type === 'single' ? '50%' : '4px',
                        border: `1.5px solid ${isSelected ? accentColor : 'rgba(0,0,0,0.25)'}`,
                        bgcolor: isSelected ? accentColor : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isSelected && <Box sx={{ width: g.selection_type === 'single' ? 7 : 10, height: g.selection_type === 'single' ? 7 : 10, borderRadius: g.selection_type === 'single' ? '50%' : '2px', bgcolor: 'white' }} />}
                      </Box>
                      <Typography sx={{ fontSize: '0.86rem', fontWeight: isSelected ? 700 : 500 }}>{c.name}</Typography>
                    </Box>
                    {parseFloat(c.price_delta) > 0 && (
                      <Typography sx={{ fontSize: '0.78rem', color: '#666', fontWeight: 700 }}>
                        +{fmtPrice(c.price_delta)} €
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })}

        {err && (
          <Box sx={{ py: 1, px: 1.5, mb: 1, bgcolor: '#fef2f2', color: '#b91c1c', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 600 }}>
            {err}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, display: 'flex', gap: 1.5 }}>
        <Box display="flex" alignItems="center" gap={0.5}
          sx={{ bgcolor: `${accentColor}10`, borderRadius: '10px', p: 0.5, border: `1px solid ${accentColor}25` }}>
          <IconButton size="small" onClick={() => setQuantity(q => Math.max(1, q - 1))}
            sx={{ width: 28, height: 28, bgcolor: accentColor, color: 'white', borderRadius: '8px', '&:hover': { opacity: 0.85 } }}>
            <RemoveIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <Typography sx={{ minWidth: 22, textAlign: 'center', fontWeight: 900, fontSize: '0.9rem', color: accentColor }}>
            {quantity}
          </Typography>
          <IconButton size="small" onClick={() => setQuantity(q => q + 1)}
            sx={{ width: 28, height: 28, bgcolor: accentColor, color: 'white', borderRadius: '8px', '&:hover': { opacity: 0.85 } }}>
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        <Button onClick={validateAndAdd} variant="contained"
          sx={{
            flex: 1, py: 1.3, borderRadius: '12px', textTransform: 'none', fontWeight: 900, fontSize: '0.88rem',
            background: accentColor, boxShadow: `0 4px 12px ${accentColor}40`,
            '&:hover': { background: accentColor, opacity: 0.92 },
          }}>
          Ajouter • {fmtPrice(linePrice)} €
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const RestaurantDetail = () => {
  const { slug: id }     = useParams();
  const navigate         = useNavigate();
  const { isAuthenticated } = useAuth();

  const [restaurant,          setRestaurant]          = useState(null);
  const [menu,                setMenu]                = useState([]);
  const cartKey = `cart_${id}`;
  const [cart,                setCart]                = useState(() => {
    try { return JSON.parse(localStorage.getItem(`cart_${id}`)) || []; } catch { return []; }
  });
  const [addresses,           setAddresses]           = useState([]);
  const [selectedAddress,     setSelectedAddress]     = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [orderType,           setOrderType]           = useState('delivery');
  const [loading,             setLoading]             = useState(true);
  const [checkoutOpen,        setCheckoutOpen]        = useState(false);
  const [scheduleOpen,        setScheduleOpen]        = useState(false);
  const [scheduleDay,         setScheduleDay]         = useState(0); // index dans les 7 prochains jours
  const [scheduleSlot,        setScheduleSlot]        = useState(null);
  const [scheduledTime,       setScheduledTime]       = useState(null);
  // Ouvre la planification sur le premier jour qui a encore des créneaux.
  const openSchedule = () => {
    const first = Array.from({ length: 7 }, (_, i) => i).find((i) => deliveryWindows(restaurant, i).length > 0);
    setScheduleDay(first ?? 0);
    setScheduleSlot(null);
    setScheduleOpen(true);
  };
  const [checkoutStep,        setCheckoutStep]        = useState(1); // 1 = infos, 2 = paiement
  const [clientSecret,        setClientSecret]        = useState('');
  const [piTotal,             setPiTotal]             = useState(0);
  const [piDeliveryFee,       setPiDeliveryFee]       = useState(0);
  const [piServiceFee,        setPiServiceFee]        = useState(0);
  const [piDistanceKm,        setPiDistanceKm]        = useState(null);
  const [piItems,             setPiItems]             = useState([]);
  const [piOrderType,         setPiOrderType]         = useState('delivery');
  const [piAddressId,         setPiAddressId]         = useState(undefined);
  // piConnectPaid retire — Usafiri encaisse 100%, virements hebdomadaires
  const [orderSuccess,        setOrderSuccess]        = useState(false);
  const [error,               setError]               = useState('');
  const [stepLoading,         setStepLoading]         = useState(false);
  const [promoCode,           setPromoCode]           = useState('');
  const [promoApplied,        setPromoApplied]        = useState(null);
  const [promoLoading,        setPromoLoading]        = useState(false);
  const [promoError,          setPromoError]          = useState('');
  const [activeCategory,      setActiveCategory]      = useState(0);
  const [itemDetailOpen,      setItemDetailOpen]      = useState(false);
  const [itemDetailItem,      setItemDetailItem]      = useState(null);
  const [mobileCartOpen,      setMobileCartOpen]      = useState(false);

  const categoryRefs = useRef({});

  useEffect(() => {
    try { localStorage.setItem(cartKey, JSON.stringify(cart)); } catch {}
  }, [cart, cartKey]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      try {
        const [rData, mData] = await Promise.all([
          restaurantService.getById(id),
          restaurantService.getMenu(id),
        ]);
        const r = rData.restaurant;
        setRestaurant({ ...r, is_open: computeIsOpen(r) });
        if (rData.restaurant?.delivery_enabled === false) setOrderType('click_and_collect');
        setMenu(mData.menu_items || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    if (isAuthenticated) {
      userService.getAddresses().then(d => {
        setAddresses(d.addresses || []);
        const def = d.addresses?.find(a => a.is_default);
        if (def) setSelectedAddress(def.id);
      }).catch(() => {});
    }
  }, [id, isAuthenticated]);

  // Genere un id unique pour chaque ligne du panier (permet plusieurs lignes du meme plat avec options differentes)
  const makeCartItemId = () => 'cart_' + Math.random().toString(36).slice(2) + Date.now().toString(36);

  // Hash des options choisies pour fusionner les lignes identiques
  const optionsHash = (snapshot) => {
    if (!snapshot || snapshot.length === 0) return '';
    return snapshot.map(g => g.group_name + ':' + g.choices.map(c => c.name).join(',')).join('|');
  };

  // Ajoute un plat sans options OU avec options deja resolues (snapshot + payload)
  const addToCart = (item, customization) => {
    // item peut etre soit un menu_item (premiere fois), soit une ligne existante du cart
    const basePrice = parseFloat(item.base_price ?? item.price) || 0;
    const customDelta = customization?.delta || 0;
    const unitPrice = parseFloat((basePrice + customDelta).toFixed(2));
    const snapshot = customization?.snapshot || null;
    const payload = customization?.payload || null;
    const hash = optionsHash(snapshot);

    // Si meme plat + memes options dans le panier -> increment quantite
    const ex = cart.find(c => c.menu_item_id === item.id && optionsHash(c.option_snapshot) === hash);
    if (ex) {
      setCart(cart.map(c => c.cartItemId === ex.cartItemId ? { ...c, quantity: c.quantity + 1 } : c));
      return;
    }
    setCart([...cart, {
      cartItemId: makeCartItemId(),
      menu_item_id: item.id,
      name: item.name,
      image_url: item.image_url,
      base_price: basePrice,
      unit_price: unitPrice,
      quantity: 1,
      option_snapshot: snapshot,
      options_payload: payload,
    }]);
  };

  const removeFromCart = (cartItemId) => {
    const ex = cart.find(c => c.cartItemId === cartItemId);
    if (!ex) return;
    setCart(ex.quantity > 1
      ? cart.map(c => c.cartItemId === cartItemId ? { ...c, quantity: c.quantity - 1 } : c)
      : cart.filter(c => c.cartItemId !== cartItemId)
    );
  };

  const incrementCart = (cartItemId) => {
    setCart(cart.map(c => c.cartItemId === cartItemId ? { ...c, quantity: c.quantity + 1 } : c));
  };

  const cartTotal        = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const cartCount        = cart.reduce((s, i) => s + i.quantity, 0);

  // ── Personnalisation : ouvre la modale ou ajoute direct ──────────────────
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customizeItem, setCustomizeItem] = useState(null);

  // ── Upsell boisson ────────────────────────────────────────────────────────
  const [drinkUpsellOpen, setDrinkUpsellOpen] = useState(false);

  const drinks = useMemo(() =>
    menu.filter(i => (i.category || '').toLowerCase().includes('boisson') && i.is_available !== false),
    [menu]
  );

  const addToCartWithUpsell = (item, customization) => {
    addToCart(item, customization);
    const isBoisson = (item.category || '').toLowerCase().includes('boisson');
    if (!isBoisson && drinks.length > 0) {
      setDrinkUpsellOpen(true);
    }
  };

  const handleAddClick = (item) => {
    if (item.option_groups && item.option_groups.length > 0) {
      setCustomizeItem(item);
      setCustomizeOpen(true);
    } else {
      addToCartWithUpsell(item);
    }
  };

  // ── Ouvrir le dialog ────────────────────────────────────────────────────────
  const openCheckout = () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (cart.length === 0) return;
    setCheckoutStep(1);
    setClientSecret('');
    setError('');
    setOrderSuccess(false);
    setCheckoutOpen(true);
  };

  const closeCheckout = () => {
    if (orderSuccess) return;
    setCheckoutOpen(false);
  };

  // ── Étape 1 → 2 : créer le PaymentIntent ────────────────────────────────────
  const handleProceedToPayment = async () => {
    if (cartTotal < 8) {
      setError('Le montant minimum de commande est de 8,00 EUR');
      return;
    }
    if (orderType === 'delivery' && !selectedAddress) {
      setError('Veuillez sélectionner une adresse de livraison');
      return;
    }
    setError('');
    setStepLoading(true);
    try {
      const data = await restaurantService.createPaymentIntent({
        restaurant_id:       restaurant?.id,
        order_type:          orderType,
        delivery_address_id: orderType === 'delivery' ? selectedAddress : undefined,
        items: cart.map(line => ({ menu_item_id: line.menu_item_id, quantity: line.quantity, options: line.options_payload || [] })),
        promo_code:          promoApplied ? promoCode.trim() : undefined,
        promo_id:            promoApplied?.promo_id,
        discount_amount:     promoApplied?.discount_amount,
      });
      const snapshotItems = cart.map(line => ({ menu_item_id: line.menu_item_id, quantity: line.quantity, special_requests: '', options: line.options_payload || [] }));
      setClientSecret(data.client_secret);
      setPiTotal(data.total);
      setPiDeliveryFee(data.delivery_fee || 0);
      setPiServiceFee(data.service_fee || 0);
      setPiDistanceKm(data.distance_km || null);
      setPiItems(snapshotItems);
      setPiOrderType(orderType);
      setPiAddressId(orderType === 'delivery' ? selectedAddress : undefined);
      setCheckoutStep(2);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur lors de la création du paiement');
    }
    setStepLoading(false);
  };

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true); setPromoError('');
    try {
      const { data } = await restaurantService.validatePromo({
        code: promoCode.trim(),
        restaurant_id: restaurant?.id,
        cart_total: cartTotal,
      });
      if (data.valid) {
        setPromoApplied(data);
        setPromoError('');
      } else {
        setPromoApplied(null);
        setPromoError(data.message || 'Code invalide');
      }
    } catch { setPromoError('Erreur lors de la vérification'); }
    finally { setPromoLoading(false); }
  };

  const cartTotalAfterPromo = promoApplied ? Math.max(0, cartTotal - promoApplied.discount_amount) : cartTotal;

  // ── Étape 2 : paiement confirmé → créer la commande ─────────────────────────
  const [paymentDone, setPaymentDone] = useState(false);

  const handlePaymentSuccess = async (paymentIntentId) => {
    setError('');
    setPaymentDone(true);
    setStepLoading(true);
    try {
      await restaurantService.createOrder({
        restaurant_id:               restaurant?.id,
        delivery_address_id:         piAddressId,
        order_type:                  piOrderType,
        items:                       piItems,
        special_instructions:        specialInstructions,
        payment_intent_id:           paymentIntentId,
      });
      setOrderSuccess(true);
      setCart([]);
      try { localStorage.removeItem(cartKey); } catch {}
      setTimeout(() => { setCheckoutOpen(false); navigate('/restaurant/orders'); }, 2500);
    } catch (e) {
      setError('Votre paiement a ete debite mais la commande n\'a pas pu etre creee. Ne payez pas a nouveau. Contactez le support avec la reference : ' + paymentIntentId);
    }
    setStepLoading(false);
  };

  const groupedMenu  = useMemo(() => menu.reduce((acc, item) => {
    const cat = item.category || 'Autres';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {}), [menu]);
  const categories = useMemo(() => Object.keys(groupedMenu), [groupedMenu]);

  const SCROLL_OFFSET = 80;

  const handleCategoryTab = (_, v) => {
    setActiveCategory(v);
    const el = categoryRefs.current[categories[v]];
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  // Met à jour le tab actif en fonction de la position de scroll
  useEffect(() => {
    if (categories.length <= 1) return;
    const onScroll = () => {
      let currentIdx = 0;
      categories.forEach((cat, idx) => {
        const el = categoryRefs.current[cat];
        if (el && el.getBoundingClientRect().top <= SCROLL_OFFSET + 10) {
          currentIdx = idx;
        }
      });
      setActiveCategory(currentIdx);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

  // Options Stripe Elements (memoised pour eviter le remontage de l'iframe)
  const accentColor = restaurant?.accent_color || C.purple;
  const stripeOptions = useMemo(() => clientSecret ? {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: accentColor,
        colorBackground: '#ffffff',
        colorText: C.text,
        borderRadius: '10px',
        fontFamily: 'Inter, sans-serif',
      },
    },
  } : null, [clientSecret, accentColor]);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh" sx={{ bgcolor: C.bg }}>
      <Box textAlign="center">
        <Box sx={{ width: 56, height: 56, borderRadius: '16px', background: `linear-gradient(135deg, ${C.purple}, ${C.gold})`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <RestaurantMenuIcon sx={{ fontSize: 28, color: 'white' }} />
        </Box>
        <CircularProgress sx={{ color: C.purple }} size={32} thickness={4} />
      </Box>
    </Box>
  );

  if (!restaurant) return (
    <Container sx={{ mt: 4 }}>
      <Alert severity="error" sx={{ borderRadius: '14px' }}>Restaurant non trouvé</Alert>
    </Container>
  );

  const hasRating = parseFloat(restaurant.rating) > 0;

  return (
    <Box sx={{ bgcolor: 'white', minHeight: '100vh' }}>

      {/* ── HERO image pure ─────────────────────────────────────────────────── */}
      <Box sx={{ position: 'relative', height: { xs: 220, sm: 300, md: 360 }, bgcolor: `${accentColor}15`, overflow: 'hidden' }}>
        {restaurant.cover_image
          ? <Box component="img" src={restaurant.cover_image} alt={restaurant.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <Box sx={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}88 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 80, opacity: 0.3 }}>
              🍽️
            </Box>
        }
        {/* Bouton retour */}
        <IconButton component={RouterLink} to="/restaurants"
          sx={{ position: 'absolute', top: 16, left: 16, bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            '&:hover': { bgcolor: '#f5f5f5' } }}>
          <ArrowBackIcon sx={{ color: C.text }} />
        </IconButton>
      </Box>

      {/* ── INFOS RESTAURANT (style Uber Eats) ─────────────────────────────── */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #f0f0f0' }}>
        <Container maxWidth="lg">
          <Box sx={{ pt: 3, pb: 2.5 }}>
            <Box display="flex" alignItems="flex-start" gap={2.5}>
              {/* Logo */}
              {restaurant.logo_url ? (
                <Box component="img" src={restaurant.logo_url} alt={restaurant.name}
                  sx={{ width: { xs: 72, sm: 88 }, height: { xs: 72, sm: 88 }, borderRadius: '16px',
                    objectFit: 'cover', border: '1px solid #eee', flexShrink: 0, boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }} />
              ) : (
                <Box sx={{ width: { xs: 72, sm: 88 }, height: { xs: 72, sm: 88 }, borderRadius: '16px', flexShrink: 0,
                  bgcolor: `${accentColor}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 36, fontWeight: 900, color: accentColor, border: `1px solid ${accentColor}20` }}>
                  {(restaurant.name || 'R')[0]}
                </Box>
              )}
              <Box flex={1}>
                {/* Nom */}
                <Typography fontWeight={900} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, color: C.text, lineHeight: 1.15, mb: 0.5 }}>
                  {restaurant.name}
                </Typography>
                {/* Tagline */}
                {restaurant.tagline && (
                  <Typography variant="body2" sx={{ color: C.muted, mb: 1 }}>{restaurant.tagline}</Typography>
                )}
                {/* Méta pills */}
                <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                  <Box display="flex" alignItems="center" gap={0.5}
                    sx={{ bgcolor: restaurant.is_open ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', px: 1, py: 0.4 }}>
                    <FiberManualRecordIcon sx={{ fontSize: 8, color: restaurant.is_open ? C.green : C.red }} />
                    <Typography variant="caption" fontWeight={700} sx={{ color: restaurant.is_open ? C.green : C.red }}>
                      {restaurant.is_open ? 'Ouvert' : 'Fermé'}
                    </Typography>
                  </Box>
                  {hasRating && (
                    <Box display="flex" alignItems="center" gap={0.4}>
                      <StarIcon sx={{ fontSize: 14, color: C.gold }} />
                      <Typography variant="caption" fontWeight={800} sx={{ color: C.text }}>{Number(restaurant.rating).toFixed(1)}</Typography>
                    </Box>
                  )}
                  {restaurant.cuisine_type && (
                    <Typography variant="caption" sx={{ color: C.muted }}>· {restaurant.cuisine_type}</Typography>
                  )}
                  {restaurant.city && (
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <LocationOnIcon sx={{ fontSize: 13, color: C.muted }} />
                      <Typography variant="caption" sx={{ color: C.muted }}>{restaurant.city}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Barre infos livraison */}
            <Box display="flex" flexWrap="wrap" gap={{ xs: 2, sm: 4 }} mt={2.5} pt={2} sx={{ borderTop: '1px solid #f0f0f0' }}>
              {[
                { icon: <DeliveryDiningIcon sx={{ fontSize: 18, color: accentColor }} />, label: 'Livraison', value: restaurant.avg_delivery_time ? `~${restaurant.avg_delivery_time} min` : '~30 min' },
                { icon: <AccessTimeIcon sx={{ fontSize: 18, color: accentColor }} />, label: 'Horaires', value: todayHoursLabel(restaurant) || 'Voir horaires' },
                { icon: <StorefrontIcon sx={{ fontSize: 18, color: accentColor }} />, label: 'Frais de livraison', value: 'Dès 2,50 €' },
              ].map((inf, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1}>
                  {inf.icon}
                  <Box>
                    <Typography variant="caption" sx={{ color: C.muted, display: 'block', lineHeight: 1 }}>{inf.label}</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: C.text }}>{inf.value}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Mini-carte localisation du restaurant */}
            {restaurant.latitude && restaurant.longitude && (
              <Box mt={2.5} sx={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #f0f0f0' }}>
                <MiniMap
                  latitude={parseFloat(restaurant.latitude)}
                  longitude={parseFloat(restaurant.longitude)}
                  height={180}
                />
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* ── BANNIERE PROMO ─────────────────────────────────────────────────── */}
      {restaurant.banner_text && (
        <Box sx={{ background: `${accentColor}10`, borderBottom: `1px solid ${accentColor}18`, py: 1, textAlign: 'center' }}>
          <Container maxWidth="lg">
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: accentColor }}>
              🎉 {restaurant.banner_text}
            </Typography>
          </Container>
        </Box>
      )}

      {/* ── NAV CATÉGORIES sticky ───────────────────────────────────────────── */}
      {categories.length > 1 && (
        <Box sx={{
          position: 'sticky', top: 0, zIndex: 10,
          bgcolor: 'white', borderBottom: '1px solid #f0f0f0',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <Container maxWidth="lg" disableGutters>
            <Box sx={{
              display: 'flex', gap: 0, overflowX: 'auto', px: { xs: 2, sm: 3 },
              scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
            }}>
              {categories.map((cat, idx) => {
                const isActive = activeCategory === idx;
                return (
                  <Box key={cat} onClick={() => handleCategoryTab(null, idx)} sx={{
                    flexShrink: 0, px: { xs: 1.5, sm: 2 }, py: 1.6, cursor: 'pointer', userSelect: 'none',
                    borderBottom: isActive ? `2px solid ${accentColor}` : '2px solid transparent',
                    transition: 'all 0.18s',
                    '&:hover': { bgcolor: `${accentColor}08` },
                  }}>
                    <Typography variant="body2" fontWeight={isActive ? 800 : 500}
                      sx={{ color: isActive ? accentColor : C.soft, whiteSpace: 'nowrap', fontSize: '0.9rem' }}>
                      {cat}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Container>
        </Box>
      )}

      {/* ── CONTENU PRINCIPAL ──────────────────────────────────────────────── */}
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 0, md: 4 }} sx={{ pb: { xs: cartCount > 0 ? 20 : 10, md: 10 }, pt: { xs: 0, md: 3 } }}>

          {/* ── MENU ────────────────────────────────────────────────────── */}
          <Grid item xs={12} md={8}>
            {Object.entries(groupedMenu).map(([category, items]) => (
              <Box key={category} ref={el => { categoryRefs.current[category] = el; }} sx={{ mb: 1 }}>
                {/* En-tête catégorie style Uber Eats */}
                <Box sx={{ px: { xs: 0, sm: 0 }, pt: 4, pb: 1.5 }}>
                  <Typography fontWeight={900} sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' }, color: C.text }}>
                    {category}
                  </Typography>
                </Box>

                {/* Items liste horizontale */}
                {items.map((item, idx) => {
                    const inCartLines = cart.filter(c => c.menu_item_id === item.id);
                    const totalQty = inCartLines.reduce((s, l) => s + l.quantity, 0);
                    const hasOptions = item.option_groups && item.option_groups.length > 0;
                    const inCart = !hasOptions && inCartLines.length === 1 ? { ...inCartLines[0], quantity: totalQty } : (totalQty > 0 ? { quantity: totalQty, _readonly: true } : null);
                    const canAdd = item.is_available && restaurant.is_open;
                    return (
                      <Box key={item.id}
                        onClick={() => { setItemDetailItem(item); setItemDetailOpen(true); }}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 2,
                          py: 2.5, px: { xs: 0, sm: 0 },
                          borderTop: idx === 0 ? '1px solid #f0f0f0' : 'none',
                          borderBottom: '1px solid #f0f0f0',
                          cursor: 'pointer', opacity: item.is_available ? 1 : 0.5,
                          transition: 'background 0.15s',
                          mx: { xs: 0, sm: 0 },
                          borderRadius: { xs: 0, sm: '4px' },
                          '&:hover': { bgcolor: '#fafafa' },
                        }}>
                        {/* Texte gauche */}
                        <Box flex={1} sx={{ minWidth: 0 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.4}>
                            <Typography fontWeight={700} sx={{ color: C.text, fontSize: '0.97rem' }}>
                              {item.name}
                            </Typography>
                            {item.is_vegetarian && (
                              <SpaIcon sx={{ fontSize: 14, color: C.green }} />
                            )}
                            {!item.is_available && (
                              <Chip label="Indisponible" size="small"
                                sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#fef2f2', color: C.red, borderRadius: '4px' }} />
                            )}
                          </Box>
                          {item.description && (
                            <Typography variant="body2" sx={{
                              color: C.muted, lineHeight: 1.5, mb: 0.8,
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            }}>
                              {item.description}
                            </Typography>
                          )}
                          <Typography fontWeight={800} sx={{ color: C.text, fontSize: '0.95rem' }}>
                            {fmt(item.price)} €
                          </Typography>
                        </Box>

                        {/* Image + bouton droite */}
                        <Box sx={{ position: 'relative', flexShrink: 0, width: { xs: 96, sm: 120 }, height: { xs: 96, sm: 120 } }}
                          onClick={e => e.stopPropagation()}>
                          {/* Image ou placeholder */}
                          <Box sx={{ width: '100%', height: '100%', borderRadius: '12px', overflow: 'hidden',
                            bgcolor: `${accentColor}08`, border: '1px solid #f0f0f0' }}>
                            {item.image_url
                              ? <Box component="img" src={item.image_url} alt={item.name}
                                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: { xs: 32, sm: 40 } }}>
                                  {getCategoryIcon(category, accentColor, 36)}
                                </Box>
                            }
                          </Box>
                          {/* Bouton + / compteur */}
                          <Box sx={{ position: 'absolute', bottom: -10, right: -4 }}
                            onClick={e => { e.stopPropagation(); }}>
                            {inCart && !inCart._readonly ? (
                              <Box display="flex" alignItems="center"
                                sx={{ bgcolor: 'white', border: `1.5px solid ${accentColor}`, borderRadius: '20px',
                                  boxShadow: `0 2px 8px ${accentColor}30`, px: 0.5, py: 0.3 }}>
                                <IconButton size="small" onClick={() => removeFromCart(inCart.cartItemId)}
                                  sx={{ width: 24, height: 24, color: accentColor }}>
                                  <RemoveIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <Typography sx={{ minWidth: 20, textAlign: 'center', fontWeight: 900, fontSize: '0.85rem', color: accentColor }}>
                                  {inCart.quantity}
                                </Typography>
                                <IconButton size="small" onClick={() => incrementCart(inCart.cartItemId)}
                                  sx={{ width: 24, height: 24, color: accentColor }}>
                                  <AddIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <IconButton onClick={() => canAdd && handleAddClick(item)} disabled={!canAdd}
                                sx={{ width: 34, height: 34, bgcolor: canAdd ? accentColor : '#e0e0e0', color: 'white',
                                  borderRadius: '50%', boxShadow: canAdd ? `0 3px 10px ${accentColor}45` : 'none',
                                  '&:hover': { bgcolor: accentColor, opacity: 0.85 },
                                  '&:disabled': { bgcolor: '#e0e0e0' }, border: '2px solid white' }}>
                                <AddIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            ))}
          </Grid>

          {/* ── PANIER SIDEBAR ──────────────────────────────────────────── */}
          <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 70 }}>
              <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #ebebeb', overflow: 'hidden' }}>
                {/* Header panier */}
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography fontWeight={800} sx={{ color: C.text, fontSize: '1rem' }}>
                    Votre panier
                  </Typography>
                  {cartCount > 0 && (
                    <Typography variant="caption" sx={{ color: C.muted, fontWeight: 600 }}>
                      {cartCount} article{cartCount > 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ p: 2.5 }}>
                  {cart.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <ShoppingCartIcon sx={{ fontSize: 40, mb: 1.5, color: '#d0d0d0' }} />
                      <Typography variant="body2" fontWeight={600} sx={{ color: C.soft, mb: 0.5 }}>Panier vide</Typography>
                      <Typography variant="caption" sx={{ color: C.muted }}>Ajoutez des plats pour commencer</Typography>
                      {!restaurant.is_open && (
                        <Box sx={{ mt: 2.5 }}>
                          {scheduledTime ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Typography variant="caption" sx={{ color: C.green, fontWeight: 700 }}>✓ Planifié : {scheduledTime}</Typography>
                              <Typography variant="caption" sx={{ color: C.muted, cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => setScheduleOpen(true)}>Modifier</Typography>
                            </Box>
                          ) : (
                            <Button size="small" onClick={openSchedule}
                              sx={{ textTransform: 'none', fontSize: '0.8rem', color: C.purple, fontWeight: 700,
                                border: `1px solid ${C.purple}30`, borderRadius: '8px', px: 2, py: 0.7,
                                '&:hover': { bgcolor: `${C.purple}08` } }}>
                              Planifier une livraison →
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <>
                      <Box mb={2}>
                        {cart.map(line => (
                          <Box key={line.cartItemId} sx={{ py: 1.4, borderBottom: '1px solid #f5f5f5' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                              <Box display="flex" alignItems="flex-start" gap={1.5} flex={1}>
                                <Box sx={{ width: 26, height: 26, borderRadius: '6px', bgcolor: '#f5f5f5', color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800, flexShrink: 0, mt: 0.1 }}>
                                  {line.quantity}
                                </Box>
                                <Typography variant="body2" fontWeight={600} sx={{ color: C.text, flex: 1, lineHeight: 1.4 }}>{line.name}</Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.4} ml={1} flexShrink={0}>
                                <Typography variant="body2" fontWeight={700} sx={{ color: C.text }}>{fmt(line.unit_price * line.quantity)} €</Typography>
                                <IconButton size="small" onClick={() => removeFromCart(line.cartItemId)}
                                  sx={{ width: 20, height: 20, color: C.muted, '&:hover': { color: C.text } }}>
                                  <RemoveIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => incrementCart(line.cartItemId)}
                                  sx={{ width: 20, height: 20, color: C.muted, '&:hover': { color: C.text } }}>
                                  <AddIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Box>
                            </Box>
                            {line.option_snapshot?.length > 0 && (
                              <Box sx={{ pl: 4.5, mt: 0.4 }}>
                                {line.option_snapshot.map((g, gi) => (
                                  <Typography key={gi} sx={{ fontSize: '0.7rem', color: C.muted, lineHeight: 1.5 }}>
                                    {g.choices.map(c => c.name).join(', ')}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>

                      {/* Type de commande */}
                      <Box sx={{ display: 'flex', mb: 2.5, borderRadius: '10px', overflow: 'hidden', border: '1px solid #ebebeb', bgcolor: '#f8f8f8' }}>
                        {[
                          { value: 'delivery',          label: 'Livraison',  icon: <LocalShippingIcon sx={{ fontSize: 15 }} /> },
                          { value: 'click_and_collect', label: 'À emporter', icon: <StorefrontIcon    sx={{ fontSize: 15 }} /> },
                        ].filter(o => o.value !== 'delivery' || restaurant?.delivery_enabled !== false).map(({ value, label, icon }) => (
                          <Box key={value} onClick={() => setOrderType(value)} sx={{
                            flex: 1, py: 1.1, cursor: 'pointer', textAlign: 'center',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6,
                            bgcolor: orderType === value ? C.text : 'transparent',
                            color: orderType === value ? 'white' : C.muted,
                            fontWeight: orderType === value ? 700 : 500, fontSize: '0.8rem',
                            borderRadius: '8px', transition: 'all 0.2s', m: 0.4,
                          }}>
                            {icon}{label}
                          </Box>
                        ))}
                      </Box>

                      {/* Totaux */}
                      <Box sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" mb={0.8}>
                          <Typography variant="body2" sx={{ color: C.muted }}>Sous-total</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: C.text }}>{fmt(cartTotal)} €</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1.2}>
                          <Typography variant="body2" sx={{ color: C.muted }}>
                            {orderType === 'click_and_collect' ? 'À emporter' : 'Livraison'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: C.muted }}>
                            {orderType === 'click_and_collect' ? 'Gratuit' : 'Selon distance'}
                          </Typography>
                        </Box>
                        <Divider sx={{ mb: 1.2 }} />
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body1" fontWeight={800} sx={{ color: C.text }}>Total</Typography>
                          <Typography variant="body1" fontWeight={800} sx={{ color: C.text }}>{fmt(cartTotal)} €</Typography>
                        </Box>
                      </Box>

                      <Button fullWidth variant="contained" size="large"
                        onClick={openCheckout}
                        disabled={!restaurant.is_open || cartTotal < 8}
                        sx={{
                          py: 1.6, borderRadius: '12px', textTransform: 'none', fontWeight: 800, fontSize: '0.95rem',
                          bgcolor: C.text, color: 'white',
                          boxShadow: 'none',
                          '&:hover': { bgcolor: C.soft, boxShadow: 'none' },
                          '&:disabled': { bgcolor: '#e0e0e0', color: '#a0a0a0', boxShadow: 'none' },
                        }}>
                        {!restaurant.is_open ? 'Restaurant fermé' : cartTotal < 8 ? `Minimum 8,00 € · ${fmt(cartTotal)} €` : `Commander · ${fmt(cartTotal)} €`}
                      </Button>

                      {!restaurant.is_open && (
                        <Box sx={{ mt: 1, textAlign: 'center' }}>
                          {scheduledTime ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Typography variant="caption" sx={{ color: C.green, fontWeight: 700 }}>
                                ✓ Planifié : {scheduledTime}
                              </Typography>
                              <Typography variant="caption" sx={{ color: C.muted, cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => setScheduleOpen(true)}>Modifier</Typography>
                            </Box>
                          ) : (
                            <Button size="small" onClick={openSchedule}
                              sx={{ textTransform: 'none', fontSize: '0.8rem', color: C.purple, fontWeight: 700,
                                border: `1px solid ${C.purple}30`, borderRadius: '8px', px: 2, py: 0.5,
                                '&:hover': { bgcolor: `${C.purple}08` } }}>
                              Planifier une livraison →
                            </Button>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* ── BARRE FLOTTANTE PANIER MOBILE ───────────────────────────────────── */}
      {cartCount > 0 && (
        <Box sx={{ display: { xs: 'block', md: 'none' }, position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200, p: 2, pb: 'calc(env(safe-area-inset-bottom) + 16px)', background: 'linear-gradient(to top, white 80%, transparent)' }}>
          <Button fullWidth variant="contained" size="large"
            onClick={() => setMobileCartOpen(true)}
            sx={{
              py: 1.8, borderRadius: '16px', textTransform: 'none', fontWeight: 800, fontSize: '1rem',
              bgcolor: C.text, color: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
              display: 'flex', justifyContent: 'space-between', px: 2.5,
              '&:hover': { bgcolor: C.soft },
            }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '8px', px: 1.2, py: 0.3, fontSize: '0.85rem', fontWeight: 900 }}>
              {cartCount}
            </Box>
            <Box>Voir le panier</Box>
            <Box fontWeight={900}>{fmt(cartTotal)} €</Box>
          </Button>
        </Box>
      )}

      {/* ── DRAWER PANIER MOBILE ─────────────────────────────────────────────── */}
      <Drawer anchor="bottom" open={mobileCartOpen} onClose={() => setMobileCartOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '85vh' } }}>
        <Box sx={{ px: 2.5, pt: 1.5, pb: 'calc(env(safe-area-inset-bottom) + 16px)', overflowY: 'auto' }}>
          {/* Handle */}
          <Box sx={{ width: 40, height: 4, bgcolor: '#e0e0e0', borderRadius: 2, mx: 'auto', mb: 2 }} />

          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography fontWeight={800} sx={{ fontSize: '1.15rem' }}>
              Votre panier · <span style={{ color: C.muted, fontWeight: 600, fontSize: '0.9rem' }}>{cartCount} article{cartCount > 1 ? 's' : ''}</span>
            </Typography>
            <IconButton size="small" onClick={() => setMobileCartOpen(false)} sx={{ bgcolor: '#f5f5f5' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Items */}
          <Box mb={2}>
            {cart.map(line => (
              <Box key={line.cartItemId} sx={{ py: 1.4, borderBottom: '1px solid #f5f5f5' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '6px', bgcolor: '#f5f5f5', color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800, flexShrink: 0 }}>
                      {line.quantity}
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ color: C.text, flex: 1 }}>{line.name}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5} ml={1}>
                    <Typography variant="body2" fontWeight={700}>{fmt(line.unit_price * line.quantity)} €</Typography>
                    <IconButton size="small" onClick={() => removeFromCart(line.cartItemId)} sx={{ width: 22, height: 22, color: C.muted }}>
                      <RemoveIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => incrementCart(line.cartItemId)} sx={{ width: 22, height: 22, color: C.muted }}>
                      <AddIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                </Box>
                {line.option_snapshot?.length > 0 && (
                  <Box sx={{ pl: 4.5, mt: 0.4 }}>
                    {line.option_snapshot.map((g, gi) => (
                      <Typography key={gi} sx={{ fontSize: '0.7rem', color: C.muted }}>{g.choices.map(c => c.name).join(', ')}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* Type de commande */}
          <Box sx={{ display: 'flex', mb: 2.5, borderRadius: '10px', overflow: 'hidden', border: '1px solid #ebebeb', bgcolor: '#f8f8f8' }}>
            {[
              { value: 'delivery',          label: 'Livraison',  icon: <LocalShippingIcon sx={{ fontSize: 15 }} /> },
              { value: 'click_and_collect', label: 'À emporter', icon: <StorefrontIcon    sx={{ fontSize: 15 }} /> },
            ].filter(o => o.value !== 'delivery' || restaurant?.delivery_enabled !== false).map(({ value, label, icon }) => (
              <Box key={value} onClick={() => setOrderType(value)} sx={{
                flex: 1, py: 1.1, cursor: 'pointer', textAlign: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6,
                bgcolor: orderType === value ? C.text : 'transparent',
                color: orderType === value ? 'white' : C.muted,
                fontWeight: orderType === value ? 700 : 500, fontSize: '0.8rem',
                borderRadius: '8px', transition: 'all 0.2s', m: 0.4,
              }}>
                {icon}{label}
              </Box>
            ))}
          </Box>

          {/* Totaux */}
          <Box sx={{ mb: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={0.8}>
              <Typography variant="body2" sx={{ color: C.muted }}>Sous-total</Typography>
              <Typography variant="body2" fontWeight={600}>{fmt(cartTotal)} €</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1.2}>
              <Typography variant="body2" sx={{ color: C.muted }}>
                {orderType === 'click_and_collect' ? 'À emporter' : 'Livraison'}
              </Typography>
              <Typography variant="body2" sx={{ color: C.muted }}>
                {orderType === 'click_and_collect' ? 'Gratuit' : 'Selon distance'}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1.2 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight={800}>Total</Typography>
              <Typography variant="body1" fontWeight={800}>{fmt(cartTotal)} €</Typography>
            </Box>
          </Box>

          <Button fullWidth variant="contained" size="large"
            onClick={() => { setMobileCartOpen(false); openCheckout(); }}
            disabled={!restaurant.is_open || cartTotal < 8}
            sx={{
              py: 1.8, borderRadius: '14px', textTransform: 'none', fontWeight: 800, fontSize: '1rem',
              bgcolor: C.text, color: 'white', boxShadow: 'none',
              '&:hover': { bgcolor: C.soft }, '&:disabled': { bgcolor: '#e0e0e0', color: '#a0a0a0' },
            }}>
            {!restaurant.is_open ? 'Restaurant fermé' : cartTotal < 8 ? `Minimum 8,00 € · ${fmt(cartTotal)} €` : `Commander · ${fmt(cartTotal)} €`}
          </Button>
        </Box>
      </Drawer>

      {/* ── Modal planification ──────────────────────────────────────────────── */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} fullWidth maxWidth="md"
        PaperProps={{ sx: { borderRadius: '20px', m: 2, overflow: 'hidden', bgcolor: '#fff', maxWidth: 680 } }}>
        <DialogContent sx={{ p: 0, bgcolor: '#fff' }}>

          {/* Header */}
          <Box sx={{ px: 3, pt: 3, pb: 2, position: 'relative' }}>
            <IconButton onClick={() => setScheduleOpen(false)} size="small"
              sx={{ position: 'absolute', top: 16, right: 16, bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#ebebeb' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography sx={{ fontSize: '1.45rem', fontWeight: 900, mb: 0.5 }}>Planifier une livraison</Typography>
            {(() => {
              const s = slotsForDay(restaurant, scheduleDay);
              if (!s) return null;
              return (
                <Typography sx={{ fontSize: '0.83rem', color: C.muted }}>
                  {s.length === 0
                    ? `${restaurant.name} est fermé ce jour-là.`
                    : `${restaurant.name} : ${s.map((x) => `${x.open} – ${x.close}`).join(' · ')}.`}
                </Typography>
              );
            })()}
          </Box>

          {/* Sélecteur de jour */}
          <Box sx={{ px: 3, mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1,
              '&::-webkit-scrollbar': { display: 'none' } }}>
              {Array.from({ length: 7 }, (_, i) => {
                const d = mayotteDate(i);
                const label = i === 0 ? "Aujourd'hui" : i === 1 ? 'Demain'
                  : d.toLocaleDateString('fr-FR', { weekday: 'short' });
                const date = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                const sel = scheduleDay === i;
                const closed = deliveryWindows(restaurant, i).length === 0;
                return (
                  <Box key={i} onClick={() => { if (!closed) { setScheduleDay(i); setScheduleSlot(null); } }}
                    aria-disabled={closed}
                    sx={{ minWidth: 88, flexShrink: 0, cursor: closed ? 'default' : 'pointer', textAlign: 'center',
                      border: `2px solid ${sel ? C.purple : '#e8e8e8'}`,
                      borderRadius: '14px', py: 1.2, px: 1,
                      bgcolor: sel ? `${C.purple}10` : '#fff',
                      opacity: closed ? 0.45 : 1,
                      transition: 'all 0.15s',
                      '&:hover': closed ? {} : { borderColor: C.purple, bgcolor: `${C.purple}08` } }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, lineHeight: 1.2,
                      color: sel ? C.purple : '#1a1a1a' }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.73rem', mt: 0.3,
                      color: sel ? C.purple : '#9e9e9e', opacity: sel ? 0.7 : 1 }}>{closed ? 'Fermé' : date}</Typography>
                  </Box>
                );
              })}
              <Box sx={{ minWidth: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChevronRightIcon sx={{ color: '#bbb', fontSize: 22 }} />
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Créneaux horaires */}
          <Box sx={{ overflowY: 'visible' }}>
            {(() => {
              const slots = deliveryWindows(restaurant, scheduleDay);
              if (slots.length === 0) {
                return (
                  <Typography sx={{ px: 3, py: 4, textAlign: 'center', color: C.muted, fontSize: '0.9rem' }}>
                    Plus aucun créneau disponible ce jour-là. Choisissez un autre jour.
                  </Typography>
                );
              }
              return slots.map((slot, i) => {
                const sel = scheduleSlot === slot;
                return (
                  <Box key={i} onClick={() => setScheduleSlot(slot)}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      py: 1.6, px: 3, cursor: 'pointer', transition: 'background 0.1s',
                      bgcolor: sel ? '#f9f9f9' : 'transparent',
                      borderBottom: '1px solid #f0f0f0',
                      '&:hover': { bgcolor: '#f9f9f9' } }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: sel ? 700 : 500, color: '#1a1a1a' }}>
                      {slot}
                    </Typography>
                    <Box sx={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      border: `2px solid ${sel ? C.purple : '#ccc'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: sel ? C.purple : 'transparent', transition: 'all 0.15s',
                    }}>
                      {sel && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff' }} />}
                    </Box>
                  </Box>
                );
              });
            })()}
          </Box>

          {/* Actions */}
          <Box sx={{ p: 3, pt: 2 }}>
            <Button fullWidth disabled={!scheduleSlot}
              onClick={() => {
                const d = mayotteDate(scheduleDay);
                const day = scheduleDay === 0 ? "Aujourd'hui" : scheduleDay === 1 ? 'Demain'
                  : d.toLocaleDateString('fr-FR', { weekday: 'long' });
                setScheduledTime(`${day} ${scheduleSlot}`);
                setScheduleOpen(false);
              }}
              sx={{ bgcolor: '#1a1a1a', color: '#fff', borderRadius: '14px', py: 1.6, fontWeight: 800,
                fontSize: '1rem', textTransform: 'none', mb: 1.2, boxShadow: 'none',
                '&:hover': { bgcolor: '#333', boxShadow: 'none' },
                '&:disabled': { bgcolor: '#ebebeb', color: '#bbb', boxShadow: 'none' } }}>
              Planifier
            </Button>
            <Button fullWidth onClick={() => setScheduleOpen(false)}
              sx={{ color: '#1a1a1a', fontWeight: 700, textTransform: 'none', fontSize: '1rem',
                borderRadius: '14px', py: 1.2, '&:hover': { bgcolor: '#f5f5f5' } }}>
              Annuler
            </Button>
          </Box>

        </DialogContent>
      </Dialog>

      {/* ── Bouton panier flottant mobile ─────────────────────────────────────── */}
      {cart.length > 0 && (
        <Box sx={{ position: 'fixed', bottom: 20, left: 16, right: 16, display: { xs: 'block', md: 'none' }, zIndex: 1000 }}>
          <Button fullWidth variant="contained" size="large" onClick={openCheckout}
            disabled={!restaurant.is_open}
            sx={{
              py: 1.8, borderRadius: '16px', boxShadow: `0 4px 20px ${accentColor}55`,
              bgcolor: accentColor, color: '#ffffff',
              textTransform: 'none', fontWeight: 800,
              '&:hover': { bgcolor: accentColor, opacity: 0.9, boxShadow: `0 6px 24px ${accentColor}77` },
              '&:disabled': { bgcolor: '#e0e0e0', color: '#a0a0a0', boxShadow: 'none' },
            }}>
            <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
              <Box display="flex" alignItems="center" gap={1.5}>
                <Badge badgeContent={cartCount} sx={{ '& .MuiBadge-badge': { bgcolor: '#ffffff', color: accentColor, fontWeight: 900 } }}>
                  <ShoppingCartIcon sx={{ color: '#ffffff' }} />
                </Badge>
                <Typography fontWeight={800} sx={{ color: '#ffffff' }}>Voir le panier</Typography>
              </Box>
              <Typography fontWeight={900} sx={{ color: '#ffffff' }}>{fmt(cartTotal)} €</Typography>
            </Box>
          </Button>
        </Box>
      )}

      {/* ── Dialog commande ────────────────────────────────────────── */}
      <Dialog
        open={checkoutOpen}
        onClose={closeCheckout}
        maxWidth="sm"
        fullWidth
        fullScreen={window.innerWidth < 600}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: '24px' }, overflow: 'hidden', m: { xs: 0, sm: 2 } } }}
      >
        {/* Header blanc épuré */}
        <Box sx={{ bgcolor: 'white', px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #f0f0f0' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              {orderSuccess
                ? <CheckCircleIcon sx={{ color: C.green, fontSize: 24 }} />
                : checkoutStep === 2
                ? <LockIcon sx={{ color: C.muted, fontSize: 22 }} />
                : <ShoppingCartIcon sx={{ color: C.text, fontSize: 22 }} />}
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: C.text, lineHeight: 1.2, fontSize: '1.1rem' }}>
                  {orderSuccess ? 'Commande confirmée !' : checkoutStep === 1 ? 'Ma commande' : 'Paiement sécurisé'}
                </Typography>
                {!orderSuccess && (
                  <Typography variant="caption" sx={{ color: C.muted }}>
                    {checkoutStep === 1 ? 'Étape 1 / 2' : 'Étape 2 / 2'}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={closeCheckout} size="small" sx={{ color: C.muted, '&:hover': { color: C.text, bgcolor: '#f5f5f5' } }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Indicateur d'étape */}
          {!orderSuccess && (
            <Box display="flex" gap={1} mt={2}>
              {[1, 2].map(s => (
                <Box key={s} sx={{ height: 3, flex: 1, borderRadius: '4px', bgcolor: checkoutStep >= s ? accentColor : '#e8e8e8', transition: 'all 0.3s' }} />
              ))}
            </Box>
          )}
        </Box>

        <DialogContent sx={{ px: 3, py: 3, bgcolor: '#f6f6f6' }}>
          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: '14px' }} onClose={() => setError('')}>{error}</Alert>}

          {/* ── SUCCÈS ── */}
          {orderSuccess && (
            <Box textAlign="center" py={4}>
              <CelebrationIcon sx={{ fontSize: 72, mb: 2, color: C.gold }} />
              <Typography variant="h6" fontWeight={900} sx={{ color: C.green, mb: 1 }}>Commande confirmée !</Typography>
              <Typography variant="body2" sx={{ color: C.muted }}>Votre paiement a été accepté. Vous serez redirigé vers vos commandes…</Typography>
              <CircularProgress size={22} sx={{ color: C.purple, mt: 2.5 }} />
            </Box>
          )}

          {/* ── ÉTAPE 1 ── */}
          {!orderSuccess && checkoutStep === 1 && (
            <>
              {/* Toggle livraison / à emporter */}
              <Box sx={{ display: 'flex', mb: 3, borderRadius: '16px', overflow: 'hidden', border: `2px solid ${C.purple}20`, bgcolor: 'white', boxShadow: `0 2px 8px ${C.purple}10` }}>
                {[
                  { value: 'delivery',          label: 'Livraison',  icon: <LocalShippingIcon sx={{ fontSize: 20 }} /> },
                  { value: 'click_and_collect', label: 'À emporter', icon: <StorefrontIcon    sx={{ fontSize: 20 }} /> },
                ].filter(o => o.value !== 'delivery' || restaurant?.delivery_enabled !== false).map(({ value, label, icon }) => (
                  <Box key={value} onClick={() => setOrderType(value)} sx={{
                    flex: 1, py: 1.8, cursor: 'pointer', textAlign: 'center',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                    transition: 'all 0.2s',
                    bgcolor: orderType === value ? C.purple : 'transparent',
                    color:   orderType === value ? 'white'  : C.soft,
                    borderRadius: '14px',
                    m: 0.5,
                  }}>
                    {icon}
                    <Typography variant="body2" fontWeight={800} sx={{ color: 'inherit', fontSize: '0.85rem' }}>{label}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Résumé articles */}
              <Box sx={{ bgcolor: 'white', borderRadius: '16px', p: 2.5, mb: 2.5, boxShadow: `0 2px 8px rgba(0,0,0,0.06)` }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: C.purple, mb: 2 }}>Votre commande</Typography>
                {cart.map(item => (
                  <Box key={item.cartItemId || item.id} display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box sx={{ minWidth: 28, height: 28, borderRadius: '8px', bgcolor: `${C.purple}12`, color: C.purple, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 900 }}>
                        {item.quantity}
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: C.text }}>{item.name}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={800} sx={{ color: C.text, ml: 1, flexShrink: 0 }}>
                      {fmt((item.unit_price ?? item.price ?? 0) * item.quantity)} €
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 1.5 }} />
                <Box display="flex" justifyContent="space-between" mb={0.8}>
                  <Typography variant="body2" sx={{ color: C.muted }}>Sous-total</Typography>
                  <Typography variant="body2" fontWeight={700}>{fmt(cartTotal)} €</Typography>
                </Box>
                {promoApplied && (
                  <Box display="flex" justifyContent="space-between" mb={0.8}>
                    <Typography variant="body2" sx={{ color: C.green, fontWeight: 700 }}>
                      Code promo ({promoCode.toUpperCase()})
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: C.green }}>
                      -{fmt(promoApplied.discount_amount)} €
                    </Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" sx={{ color: C.muted }}>
                    {orderType === 'click_and_collect' ? 'À emporter' : 'Livraison'}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: orderType === 'click_and_collect' ? C.green : C.muted }}>
                    {orderType === 'click_and_collect' ? 'Gratuit' : 'Calculés selon distance'}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" sx={{ bgcolor: `${C.purple}06`, borderRadius: '10px', px: 1.5, py: 1 }}>
                  <Typography variant="body1" fontWeight={900} sx={{ color: C.text }}>Total</Typography>
                  <Typography variant="body1" fontWeight={900} sx={{ color: C.purple }}>{fmt(cartTotalAfterPromo)} €</Typography>
                </Box>

                {/* ── Code promo ── */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                  {promoApplied ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: `${C.green}10`, borderRadius: '10px', px: 1.5, py: 1 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: C.green }}>✓ Code appliqué : {promoCode.toUpperCase()}</Typography>
                      <Button size="small" onClick={() => { setPromoApplied(null); setPromoCode(''); }}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', color: C.muted, minWidth: 0, px: 1 }}>Retirer</Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small" fullWidth placeholder="Code promo" value={promoCode}
                        onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                        onKeyDown={e => e.key === 'Enter' && applyPromo()}
                        error={!!promoError}
                        helperText={promoError}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.84rem', fontFamily: 'monospace' } }}
                      />
                      <Button onClick={applyPromo} disabled={promoLoading || !promoCode.trim()}
                        sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', bgcolor: C.text, color: 'white', borderRadius: '10px', px: 2, flexShrink: 0, '&:hover': { bgcolor: '#333' }, '&:disabled': { bgcolor: '#e0e0e0' } }}>
                        {promoLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Appliquer'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Adresse / Retrait */}
              <Box sx={{ bgcolor: 'white', borderRadius: '16px', p: 2.5, mb: 2.5, boxShadow: `0 2px 8px rgba(0,0,0,0.06)` }}>
                {orderType === 'delivery' ? (
                  <>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: C.text, mb: 1.5 }}>
                      Adresse de livraison
                    </Typography>
                    {addresses.length > 0 ? (
                      <TextField select fullWidth value={selectedAddress} onChange={e => setSelectedAddress(e.target.value)}
                        SelectProps={{ native: true }}
                        sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} required>
                        <option value="">Choisir une adresse…</option>
                        {addresses.map(addr => (
                          <option key={addr.id} value={addr.id}>{addr.label} — {addr.address_line1}, {addr.city}</option>
                        ))}
                      </TextField>
                    ) : (
                      <Alert severity="info" sx={{ mb: 1, borderRadius: '12px' }}>Aucune adresse enregistrée.</Alert>
                    )}
                    <Button size="small" onClick={() => navigate('/profile/addresses')}
                      sx={{ color: C.purple, textTransform: 'none', fontSize: '0.8rem', fontWeight: 700 }}>
                      + Gérer mes adresses
                    </Button>
                  </>
                ) : (
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <LocationOnIcon sx={{ color: C.purple, mt: 0.2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ color: C.text, mb: 0.4 }}>Retrait au restaurant</Typography>
                      <Typography variant="body2" sx={{ color: C.soft }}>{restaurant.address}{restaurant.city ? `, ${restaurant.city}` : ''}</Typography>
                      <Typography variant="caption" sx={{ color: C.muted, mt: 0.5, display: 'block' }}>Un code de retrait vous sera envoyé</Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Instructions */}
              <Box sx={{ bgcolor: 'white', borderRadius: '16px', p: 2.5, boxShadow: `0 2px 8px rgba(0,0,0,0.06)` }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: C.text, mb: 1 }}>
                  Instructions{' '}
                  <Typography component="span" variant="caption" sx={{ color: C.muted, fontWeight: 400 }}>(optionnel)</Typography>
                </Typography>
                <TextField fullWidth multiline rows={2} value={specialInstructions} onChange={e => setSpecialInstructions(e.target.value)}
                  placeholder="Sans piment, sonner 2 fois…"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f9f9fb' } }} />
              </Box>
            </>
          )}

          {/* ── ÉTAPE 2 : paiement Stripe ── */}
          {!orderSuccess && checkoutStep === 2 && clientSecret && stripeOptions && (
            <Elements stripe={stripePromise} options={stripeOptions}>
              <StripePaymentForm
                onPaid={handlePaymentSuccess}
                onError={(msg) => setError(msg)}
                total={piTotal}
                cartTotal={cartTotal}
                deliveryFee={piDeliveryFee}
                serviceFee={piServiceFee}
                distanceKm={piDistanceKm}
                orderType={orderType}
                paymentDone={paymentDone}
              />
            </Elements>
          )}
        </DialogContent>

        {/* ── Bouton principal ── */}
        {!orderSuccess && checkoutStep === 1 && (
          <Box sx={{ px: 3, pb: 3, pt: 1, bgcolor: '#f9f9fb' }}>
            <Button
              fullWidth
              onClick={handleProceedToPayment}
              variant="contained"
              size="large"
              disabled={stepLoading || (orderType === 'delivery' && !selectedAddress)}
              sx={{
                py: 2, borderRadius: '14px', textTransform: 'none', fontWeight: 800, fontSize: '1rem',
                bgcolor: C.text, color: 'white', boxShadow: 'none',
                '&:hover': { bgcolor: C.soft, boxShadow: 'none' },
                '&:disabled': { bgcolor: '#e0e0e0', color: '#a0a0a0', boxShadow: 'none' },
              }}>
              {stepLoading
                ? <CircularProgress size={22} sx={{ color: 'white' }} />
                : <Box display="flex" alignItems="center" gap={1.5}><LockIcon sx={{ fontSize: 18 }} /> Procéder au paiement</Box>
              }
            </Button>
          </Box>
        )}

        {!orderSuccess && checkoutStep === 2 && (
          <Box sx={{ px: 3, pb: 2, pt: 0.5, bgcolor: '#f9f9fb' }}>
            <Button onClick={() => { setCheckoutStep(1); setError(''); }}
              sx={{ color: C.muted, textTransform: 'none', borderRadius: '12px', fontWeight: 700 }}>
              ← Retour
            </Button>
          </Box>
        )}
      </Dialog>

      <CustomizeDialog
        open={customizeOpen}
        item={customizeItem}
        accentColor={accentColor}
        onClose={() => setCustomizeOpen(false)}
        onAdd={(it, customization) => { setCustomizeOpen(false); addToCartWithUpsell(it, customization); }}
      />

      {/* ── Upsell boisson ──────────────────────────────────────────────────── */}
      <Dialog open={drinkUpsellOpen} onClose={() => setDrinkUpsellOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography fontWeight={800} sx={{ fontSize: '1rem', color: C.text }}>Ajouter une boisson ?</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: C.muted, mt: 0.3 }}>Complétez votre commande</Typography>
          </Box>
          <IconButton size="small" onClick={() => setDrinkUpsellOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {drinks.map(drink => (
            <Box key={drink.id}
              onClick={() => { addToCart(drink); setDrinkUpsellOpen(false); }}
              sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5, cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                '&:hover': { bgcolor: '#f9f9f9' } }}>
              {drink.image_url
                ? <Box component="img" src={drink.image_url} sx={{ width: 52, height: 52, borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
                : <Box sx={{ width: 52, height: 52, borderRadius: '10px', bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <LocalBarIcon sx={{ color: C.muted, fontSize: 24 }} />
                  </Box>
              }
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={700} sx={{ fontSize: '0.9rem', color: C.text }}>{drink.name}</Typography>
                {drink.description && <Typography sx={{ fontSize: '0.75rem', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{drink.description}</Typography>}
              </Box>
              <Typography fontWeight={800} sx={{ fontSize: '0.9rem', color: C.text, flexShrink: 0 }}>{parseFloat(drink.price).toFixed(2)} €</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button fullWidth variant="outlined" onClick={() => setDrinkUpsellOpen(false)}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, borderColor: '#e0e0e0', color: C.soft }}>
            Non merci
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal détail produit ─────────────────────────────────────────── */}
      {itemDetailItem && (
        <Dialog
          open={itemDetailOpen}
          onClose={() => setItemDetailOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={window.innerWidth < 600}
          PaperProps={{ sx: { borderRadius: { xs: 0, sm: '24px' }, overflow: 'hidden', m: { xs: 0, sm: 2 }, maxHeight: { sm: '90vh' } } }}
        >
          {(() => {
            const item = itemDetailItem;
            const inCartLines = cart.filter(c => c.menu_item_id === item.id);
            const totalQty = inCartLines.reduce((s, l) => s + l.quantity, 0);
            const hasOptions = item.option_groups && item.option_groups.length > 0;
            const inCart = !hasOptions && inCartLines.length === 1 ? { ...inCartLines[0], quantity: totalQty } : (totalQty > 0 ? { quantity: totalQty, _readonly: true } : null);
            const canAdd = item.is_available && restaurant.is_open;

            return (
              <>
                {/* Image hero */}
                <Box sx={{ position: 'relative', height: { xs: 260, sm: 300 }, flexShrink: 0, bgcolor: `${accentColor}08` }}>
                  {item.image_url ? (
                    <Box component="img" src={item.image_url} alt={item.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{
                      width: '100%', height: '100%',
                      background: `linear-gradient(135deg, ${accentColor}15 0%, ${C.gold}10 100%)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 96,
                    }}>
                      {getCategoryIcon(item.category, accentColor, 96)}
                    </Box>
                  )}
                  {/* Dégradé bas */}
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)' }} />
                  {/* Bouton fermer */}
                  <IconButton onClick={() => setItemDetailOpen(false)}
                    sx={{ position: 'absolute', top: 12, right: 12, bgcolor: 'rgba(0,0,0,0.45)', color: 'white', backdropFilter: 'blur(8px)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}>
                    <CloseIcon />
                  </IconButton>
                  {/* Badges */}
                  <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 0.8 }}>
                    {item.is_vegetarian && (
                      <Chip icon={<SpaIcon sx={{ fontSize: '12px !important', color: `${C.green} !important` }} />} label="Végétarien"
                        size="small" sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: C.green, fontWeight: 800, backdropFilter: 'blur(6px)' }} />
                    )}
                    {!item.is_available && (
                      <Chip label="Indisponible" size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: C.red, fontWeight: 800, backdropFilter: 'blur(6px)' }} />
                    )}
                  </Box>
                  {/* Prix en overlay bas */}
                  <Box sx={{ position: 'absolute', bottom: 14, left: 20 }}>
                    <Typography fontWeight={900} sx={{ color: 'white', fontSize: '1.6rem', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                      {fmt(item.price)} €
                    </Typography>
                  </Box>
                </Box>

                {/* Contenu scrollable */}
                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                  <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
                    {/* Nom + temps */}
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                      <Typography variant="h5" fontWeight={900} sx={{ color: C.text, lineHeight: 1.2, flex: 1 }}>
                        {item.name}
                      </Typography>
                      {item.preparation_time && (
                        <Box display="flex" alignItems="center" gap={0.5}
                          sx={{ bgcolor: `${accentColor}10`, borderRadius: '10px', px: 1.2, py: 0.6, flexShrink: 0 }}>
                          <AccessTimeIcon sx={{ fontSize: 14, color: accentColor }} />
                          <Typography variant="caption" fontWeight={700} sx={{ color: accentColor }}>{item.preparation_time} min</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Description complète */}
                    {item.description && (
                      <Typography variant="body1" sx={{ color: C.soft, lineHeight: 1.7, mt: 1.5, mb: 2 }}>
                        {item.description}
                      </Typography>
                    )}

                    {/* Infos options */}
                    {hasOptions && (
                      <Box sx={{ mb: 2, p: 1.5, borderRadius: '12px', bgcolor: `${accentColor}08`, border: `1px solid ${accentColor}14` }}>
                        <Typography variant="body2" fontWeight={700} sx={{ color: accentColor }}>
                          Ce plat est personnalisable — choisissez vos options après avoir cliqué sur Ajouter.
                        </Typography>
                      </Box>
                    )}

                    {/* Nom du restaurant */}
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${accentColor}10` }}>
                      {restaurant.logo_url && (
                        <Box component="img" src={restaurant.logo_url} alt={restaurant.name}
                          sx={{ width: 28, height: 28, borderRadius: '8px', objectFit: 'cover' }} />
                      )}
                      <Typography variant="body2" sx={{ color: C.muted, fontWeight: 600 }}>
                        Proposé par <strong style={{ color: C.text }}>{restaurant.name}</strong>
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Bouton action sticky */}
                <Box sx={{ px: 3, py: 2.5, borderTop: `1px solid ${accentColor}10`, bgcolor: 'white' }}>
                  {inCart && !inCart._readonly ? (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                      <IconButton onClick={() => removeFromCart(inCart.cartItemId)}
                        sx={{ width: 44, height: 44, bgcolor: accentColor, color: 'white', borderRadius: '14px', '&:hover': { opacity: 0.85 } }}>
                        <RemoveIcon />
                      </IconButton>
                      <Typography fontWeight={900} sx={{ fontSize: '1.3rem', color: accentColor, minWidth: 32, textAlign: 'center' }}>
                        {inCart.quantity}
                      </Typography>
                      <IconButton onClick={() => incrementCart(inCart.cartItemId)}
                        sx={{ width: 44, height: 44, bgcolor: accentColor, color: 'white', borderRadius: '14px', '&:hover': { opacity: 0.85 } }}>
                        <AddIcon />
                      </IconButton>
                      <Button fullWidth variant="contained" onClick={() => setItemDetailOpen(false)}
                        sx={{ py: 1.6, borderRadius: '16px', textTransform: 'none', fontWeight: 900, fontSize: '1rem',
                          background: `linear-gradient(135deg, ${accentColor}, ${C.gold})`,
                          boxShadow: `0 6px 20px ${accentColor}40`, ml: 1 }}>
                        Voir le panier · {fmt(inCart.quantity * item.price)} €
                      </Button>
                    </Box>
                  ) : (
                    <Button fullWidth variant="contained" size="large"
                      disabled={!canAdd}
                      onClick={() => { setItemDetailOpen(false); handleAddClick(item); }}
                      sx={{
                        py: 2, borderRadius: '16px', textTransform: 'none', fontWeight: 900, fontSize: '1.05rem',
                        background: canAdd ? `linear-gradient(135deg, ${accentColor}, ${C.gold})` : undefined,
                        boxShadow: canAdd ? `0 8px 24px ${accentColor}40` : 'none',
                        '&:hover': { boxShadow: `0 10px 32px ${accentColor}55` },
                      }}>
                      {canAdd
                        ? <Box display="flex" alignItems="center" gap={1.5}>
                            <AddIcon />
                            {hasOptions ? 'Personnaliser et ajouter' : `Ajouter au panier · ${fmt(item.price)} €`}
                          </Box>
                        : (restaurant.is_open ? 'Indisponible' : 'Restaurant fermé')
                      }
                    </Button>
                  )}
                </Box>
              </>
            );
          })()}
        </Dialog>
      )}
    </Box>
  );
};

export default RestaurantDetail;
